/** @jsxImportSource @emotion/react */

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { CSSObject } from '@emotion/react';
import { fonts } from '../../style/fonts';
import { colors } from '../../style/colors';
import { cardHeaderCss } from '../../style/shapes';
import { buttons } from '../../style/buttons';
import { AccordionView } from '../commons/DraggableAccordionView';
import { FaqItem, questionAndAnswer } from '../../assets/support/Q&A';
import CloseIcon from '../../assets/close.svg';

const supportModalCss: CSSObject = {
  paddingBottom: '20px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '.support-modal-footer': {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    backgroundColor: colors.backgroundDarkerGrey,
    border: `1px solid ${colors.dividerGrey}`,
    borderRadius: '10px',
    marginRight: '20px',
    marginLeft: '20px',
  },
  '.support-modal-body': {
    width: '100%',
    alignSelf: 'flex-end',
    flex: 1,
    overflow: 'auto',
    padding: '20px',
  },
};

export default function SupportModal({
  isOpen,
  setModalMode,
  onAskPerci,
  onContactUs,
}: {
  isOpen: boolean;
  setModalMode: (isOpen: boolean) => void;
  /** Opens Perci. Absent on pages without Perci, which hides the button. */
  onAskPerci?: () => void;
  /** Opens Contact us on Ask a question. */
  onContactUs?: () => void;
}) {
  let onCancel = () => setModalMode(false);
  const [expendedItemsIds, setExpendedItemsIds] = useState<string[]>([]);
  const faqList: FaqItem[] = questionAndAnswer;
  return (
    <>
      {isOpen && (
        <Dialog
          css={{
            '.MuiDialog-paper': {
              width: 'min(90vw, 800px)',
              height: '100%',
            },
            '.MuiDialog-container': { height: '100%' },
          }}
          open={true}
          onClose={() => {
            onCancel?.();
          }}
        >
          <div css={supportModalCss}>
            <div className={'support-modal-header'} css={cardHeaderCss}>
              Support
              <button type="button">
                <CloseIcon width={13} onClick={onCancel} />
              </button>
            </div>
            <div className={'support-modal-body'}>
              <div css={{ ...fonts.largeLabel, ...{ paddingBottom: '15px' } }}>
                Frequently Asked Questions
              </div>
              <div>
                <AccordionView
                  items={faqList}
                  expendedItemsIds={expendedItemsIds}
                  setExpendedItemsIds={setExpendedItemsIds}
                  getAccordionSummary={(_, item) => {
                    return <div css={fonts.largeLabel}>{item.question} </div>;
                  }}
                  getAccordionDetails={(_, item) => {
                    return (
                      <div
                        css={{
                          ...fonts.text,
                          // No fixed height: a fixed box (it was 58px) cut off any
                          // answer longer than about two lines.
                          padding: '8px 0 12px',
                          marginLeft: '20px',
                          lineHeight: 1.5,
                        }}
                      >
                        {item.answer}
                      </div>
                    );
                  }}
                  getId={(item: FaqItem) => faqList.indexOf(item).toString()}
                />
              </div>
            </div>
            <div className={'support-modal-footer'}>
              <div>
                <div css={{ ...fonts.largeLabel }}>Need additional help?</div>
                <div>
                  {onAskPerci
                    ? 'Ask Perci, or send our team a question.'
                    : 'Send our team a question.'}
                </div>
              </div>
              <div css={{ display: 'flex', gap: '12px' }}>
                {onAskPerci && (
                  <button
                    type="button"
                    css={buttons.primary}
                    onClick={onAskPerci}
                  >
                    Ask Perci
                  </button>
                )}
                {onContactUs && (
                  <button
                    type="button"
                    css={onAskPerci ? buttons.secondary : buttons.primary}
                    onClick={onContactUs}
                  >
                    Contact us
                  </button>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
